import * as React from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { omitBy } from "lodash-es";
import { casesActions, db } from "configs";
import {
  FormWatcherCallback,
  resolveDate,
  resolveString,
  useCurrentCase,
  useDispatch,
  useParams,
  useInitializeOfflineData,
  generateId
} from "duck";
import { useUpdatePatientInformationMutation } from "requests";
import { FormValues } from "./types";
import { calculateWeight } from "./utils";

export const useUpdatePatient = () => {
  const { loadBreeds, loadProcedures } = useInitializeOfflineData();
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const { patientId, shouldSync, weightMeasure } = useCurrentCase(
    medicalCase => ({
      patientId: medicalCase.data.client.patient._id,
      shouldSync: medicalCase.shouldSync,
      weightMeasure: medicalCase.data.client.patient.weightMeasure
    })
  );

  const procedures = useLiveQuery(async () => db.procedures.toArray());

  const [update] = useUpdatePatientInformationMutation({ ignoreResults: true });

  return React.useCallback<FormWatcherCallback<FormValues>>(
    values => {
      const variables = omitBy(
        {
          species: values.species,
          breed:
            values.breed === undefined
              ? values.species
                ? null
                : undefined
              : values.breed,
          sex: values.sex,
          procedures: values.procedures,
          weight: calculateWeight(weightMeasure, values.weight),
          weightMeasure: values.weightMeasure,
          name: resolveString(values.name),
          alerts: resolveString(values.alerts),
          procedureDate: resolveDate(values.procedureDate),
          dateOfBirth: resolveDate(values.dateOfBirth)
        },
        value => value === undefined
      );

      dispatch(
        casesActions.updatePatientInfo({
          _id: caseId as string,
          data: variables
        })
      );

      if (shouldSync) {
        return update({
          variables: { input: { _id: patientId, ...variables } }
        }).then(({ data }) => {
          if (values.breed) {
            loadBreeds();
          }
          if (values.procedures) {
            loadProcedures();
          }

          const shouldUpdateVaccinesTests = values.species !== undefined;

          if (shouldUpdateVaccinesTests) {
            const newVaccines =
              data?.updatePatient?.patient?.vaccines?.collection || [];

            const newTests =
              data?.updatePatient?.patient?.patientTests?.collection || [];

            const newTreatment =
              data?.updatePatient?.patient?.treatment || null;

            dispatch(
              casesActions.setVaccinesAndTests({
                _id: caseId,
                data: {
                  vaccines: newVaccines,
                  patientTests: newTests,
                  treatment: newTreatment
                }
              })
            );
          }

          const generalAssessment =
            data?.updatePatient?.patient?.generalAssessment || null;
          dispatch(
            casesActions.updateGeneralAssessment({
              data: generalAssessment
                ? {
                    ...generalAssessment,
                    beforePhotoFront:
                      generalAssessment.beforePhotoFront?._id || null,
                    beforePhotoLeft:
                      generalAssessment.beforePhotoLeft?._id || null,
                    beforePhotoRight:
                      generalAssessment.beforePhotoRight?._id || null,
                    afterPhotoFront:
                      generalAssessment.afterPhotoFront?._id || null,
                    afterPhotoLeft:
                      generalAssessment.afterPhotoLeft?._id || null,
                    afterPhotoRight:
                      generalAssessment.afterPhotoRight?._id || null,
                    additionalPhotos:
                      generalAssessment.additionalPhotos?.collection.map(
                        photo => photo._id
                      ) || [],
                    radiographicFindingsPhotos:
                      generalAssessment.radiographicFindingsPhotos?.collection.map(
                        photo => photo._id
                      ) || []
                  }
                : null,
              _id: caseId
            })
          );

          const tplo =
            data?.updatePatient?.patient
              ?.tibialPlateauLevelingOsteotomyPlanning || null;
          dispatch(casesActions.updateTPLO({ data: tplo, _id: caseId }));

          const dentalChartings =
            data?.updatePatient?.patient?.dentalChartings?.collection?.map(
              chart => ({ ...chart, tooth: chart.tooth._id })
            ) || null;

          dispatch(
            casesActions.setDentalChartings({
              data: dentalChartings,
              _id: caseId
            })
          );

          const dentalSketch =
            data?.updatePatient?.patient?.dentalSketch?._id || null;
          dispatch(
            casesActions.updateDentalSketch({
              data: { dentalSketch },
              _id: caseId
            })
          );

          const dentalSketchComments =
            data?.updatePatient?.patient?.dentalSketchComments?.collection ||
            [];
          dispatch(
            casesActions.setDentalSketchComments({
              data: dentalSketchComments,
              _id: caseId
            })
          );
        });
      }

      if (values.procedures) {
        const newProcedures = values.procedures
          .filter(pr => !procedures?.some(p => p.name === pr))
          .map(pr => ({
            name: pr,
            _id: generateId()
          }));

        if (newProcedures.length) {
          db.procedures.bulkAdd(newProcedures);
        }
      }
    },
    [patientId, update, shouldSync, weightMeasure, procedures]
  );
};
